// deps
import React, { memo, useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { isEmpty } from "@swiggy-private/js-utils";

// internal-deps
import { Tick } from "@swiggy-private/core-ui";
import { COD_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

// interfaces
import { CodPaymentMethodData, ICodPaymentGroup, TurboCodPaymentMethodMeta } from "@payment-ui/interfaces/Cod";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// widgets
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";
import Alert from "@payment-ui/widgets/alert/Alert";
import { PodConsentHalfCard } from "@payment-ui/components/turbo-payment-widget";

// components
import PayButton from "./components/PayButton";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// helpers
import { canEnablePaymentMethod, getPaymentAmount, getPaymentMethodIconUrl } from "@payment-ui/helpers/Payment";
import { getCodAriaLabel } from "@payment-ui/helpers/Cod";

// styles
import styles from "./Cod.v4.scss";

interface Props extends PaymentMethodProps {
    key: string;
    paymentAmount: string;
    methodData: CodPaymentMethodData;
    paymentActions: ICodPaymentGroup["paymentActions"];
}

const CodPaymentMethod: React.FC<Props> = (props) => {
    const { methodData, paymentActions, selected, isPaymentGroupEnabled } = props;
    const { onLoadCodHalfCard, onPlaceCodHalfCardCTa, onCancelHalfCard } = paymentActions;
    const { meta, displayName } = methodData;
    const { onSelect, onPay } = paymentActions;
    const [showHalfCard, setShowHalfCard] = useState(false);

    const isMethodEnabled = useMemo(
        () => canEnablePaymentMethod(methodData, isPaymentGroupEnabled),
        [isPaymentGroupEnabled, methodData],
    );

    const containerClass = classNames(styles.paymentMethod, {
        [styles.paymentMethodDisabled]: !isMethodEnabled,
    });

    const tickClass = classNames({
        [styles.codWithSubText]: isMethodEnabled && props.methodData?.meta?.promoMsg,
    });

    const iconUrl = useMemo(() => getPaymentMethodIconUrl(methodData), [methodData]);

    const codAriaLabel = useMemo(() => getCodAriaLabel(methodData, isMethodEnabled), [methodData, isMethodEnabled]);

    const paymentAmount = useMemo(
        () => getPaymentAmount(props.paymentAmount, methodData),
        [props.paymentAmount, methodData],
    );

    const isHalfCardEnabled = useMemo(
        () => !isEmpty(meta?.halfCardInfo) && !isEmpty(meta?.halfCard),
        [meta?.halfCardInfo, meta?.halfCard],
    );

    // Responsible for tracking events for half card
    const turboPodHalfCardEvents = useMemo(
        () => ({
            onPlaceOrderCtaClick: () => {
                onPlaceCodHalfCardCTa?.(meta.halfCard || "");
            },
            onCancelCtaClick: () => {
                onCancelHalfCard?.(meta.halfCard || "");
            },
            onLoad: () => {
                onLoadCodHalfCard?.(meta.halfCard || "");
            },
        }),
        [onLoadCodHalfCard, onPlaceCodHalfCardCTa, onCancelHalfCard, meta.halfCard],
    );

    const handleClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            if (!isMethodEnabled) return;
            onSelect(methodData);
        },
        [isMethodEnabled, methodData, onSelect],
    );

    const handlePlaceOrder = useCallback(
        (paymentMethodData: CodPaymentMethodData) => {
            if (isHalfCardEnabled) {
                setShowHalfCard(true);
                return;
            }
            onPay(paymentMethodData);
        },
        [onPay, isHalfCardEnabled],
    );

    const handleHalfCardPlaceOrder = useCallback(() => {
        if (methodData) {
            onPay(methodData);
            setShowHalfCard(false);
        }
    }, [methodData, onPay]);

    const handleHalfCardCancel = useCallback(() => {
        setShowHalfCard(false);
    }, []);

    return (
        <>
            <div className={containerClass} onClick={handleClick} data-testid={COD_PAYMENT_METHOD_TEST_IDS.CONTAINER}>
                <div className={styles.paymentMethodContainer}>
                    <PaymentMethodIcon src={iconUrl} data-testid={COD_PAYMENT_METHOD_TEST_IDS.ICON} />

                    <PaymentMethodName
                        displayName={displayName || TEXTS.CASH}
                        promoMsg={isMethodEnabled ? meta.promoMsg : ""}
                        enabled={isMethodEnabled}
                        ariaLabel={codAriaLabel}
                        data-testid={COD_PAYMENT_METHOD_TEST_IDS.NAME}
                        recommended={methodData?.meta?.selectionTagText}
                    >
                        {!isMethodEnabled && (
                            <Alert
                                className={styles.paymentMethodDisable}
                                message={methodData.meta.disableMessage}
                                data-testid={COD_PAYMENT_METHOD_TEST_IDS.DISABLE_MESSAGE}
                            />
                        )}
                    </PaymentMethodName>

                    {isMethodEnabled && (
                        <Tick
                            className={tickClass}
                            selected={selected}
                            data-testid={COD_PAYMENT_METHOD_TEST_IDS.TICK}
                        />
                    )}
                </div>

                {selected && (
                    <PayButton
                        paymentAmount={paymentAmount}
                        enabled={isMethodEnabled}
                        selected={selected}
                        methodData={methodData}
                        onPay={handlePlaceOrder}
                        ctaMsg={meta?.ctaMsg}
                    />
                )}
            </div>

            {showHalfCard && isHalfCardEnabled && (
                <PodConsentHalfCard
                    data={meta as TurboCodPaymentMethodMeta}
                    onPlaceOrder={handleHalfCardPlaceOrder}
                    onCancel={handleHalfCardCancel}
                    turboPodHalfCardEvents={turboPodHalfCardEvents}
                    isActive={true}
                />
            )}
        </>
    );
};

export default memo(CodPaymentMethod);
